import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, BRAND } from "@/lib/config";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Car, LayoutGrid, Building2, Armchair, Trash2, SquareStack, Droplets, Phone, Loader2, CheckCircle2 } from "lucide-react";

const PW_IMG =
  "https://images.unsplash.com/photo-1781637202423-33ec5b47e52e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";

const SERVICES = [
  { key: "Patios", icon: LayoutGrid },
  { key: "Driveways", icon: Car },
  { key: "Walls", icon: Building2 },
  { key: "Benches", icon: Armchair },
  { key: "Dumpsters", icon: Trash2 },
  { key: "Dumpster Pads", icon: SquareStack },
];

export default function PressureWashing() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", details: "" });
  const [services, setServices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (s) =>
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/pressure-washing", { ...form, services });
      setDone(true);
      toast.success("Request sent! We'll be in touch soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-boco-navy text-white">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold tracking-[0.15em] text-boco-green">
              <Droplets className="h-3.5 w-3.5" /> PRESSURE WASHING SERVICES
            </div>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              More Than Just <span className="text-boco-green">Bins.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-xl">
              Got a grimy driveway, dingy patio, or a dumpster pad that needs a deep clean? BOCO BINS handles
              a wide range of residential and commercial pressure washing across Buchanan, Botetourt County
              and surrounding communities.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <a href="#quote" className="rounded-full bg-boco-green px-7 py-3.5 font-bold text-boco-navy hover:bg-white transition-colors" data-testid="pw-hero-quote-btn">
                Request a Quote
              </a>
              <a href={BRAND.phoneHref} className="rounded-full border border-white/30 px-7 py-3.5 font-bold text-white hover:bg-white/10 transition-colors flex items-center gap-2">
                <Phone className="h-4 w-4" /> {BRAND.phone}
              </a>
            </div>
          </div>
          <motion.img
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            src={PW_IMG}
            alt="Freshly pressure-washed concrete driveway"
            className="rounded-3xl object-cover w-full h-64 lg:h-96 shadow-2xl"
          />
        </div>
      </section>

      {/* SERVICES */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="text-center">
          <div className="text-xs font-bold tracking-[0.2em] text-boco-green">WHAT WE CLEAN</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black text-boco-navy tracking-tight">
            Pressure Washing We Offer
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Tell us what you need cleaned — we'll give you a fair, local quote. No job too big or too small.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl bg-white border border-slate-200 p-6 flex items-center gap-4 shadow-sm"
              data-testid={`pw-service-${i}`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-boco-greenlight text-boco-greendark">
                <s.icon className="h-6 w-6" />
              </div>
              <span className="font-display font-bold text-boco-navy">{s.key}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="mx-auto max-w-3xl px-5 sm:px-8 pb-20 scroll-mt-24">
        <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-10 shadow-[0_20px_60px_-15px_rgba(11,42,91,0.25)]">
          {done ? (
            <div className="text-center py-8" data-testid="pw-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-boco-green" />
              <h3 className="mt-4 font-display text-2xl font-black text-boco-navy">Request Received!</h3>
              <p className="mt-2 text-slate-600">Thanks — we'll reach out shortly to talk through your pressure washing needs.</p>
              <Link to="/" className="mt-6 inline-flex rounded-full bg-boco-navy px-6 py-3 font-bold text-white hover:bg-boco-navylight transition-colors" data-testid="pw-home-btn">Back to Home</Link>
            </div>
          ) : (
            <>
              <h3 className="font-display text-2xl font-black text-boco-navy">Request a Pressure Washing Quote</h3>
              <p className="mt-2 text-slate-600 text-sm">Select what you need cleaned and we'll get back to you with pricing.</p>

              <div className="mt-6">
                <Label className="text-boco-navy font-semibold">What do you need cleaned?</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SERVICES.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => toggle(s.key)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold border-2 transition-colors ${services.includes(s.key) ? "border-boco-green bg-boco-greenlight text-boco-greendark" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
                      data-testid={`pw-toggle-${s.key.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {s.key}
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">
                <div><Label className="text-boco-navy font-semibold">Full Name *</Label><Input className="mt-1.5 text-slate-900" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pw-name" /></div>
                <div><Label className="text-boco-navy font-semibold">Phone *</Label><Input className="mt-1.5 text-slate-900" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="pw-phone" /></div>
                <div><Label className="text-boco-navy font-semibold">Email</Label><Input type="email" className="mt-1.5 text-slate-900" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pw-email" /></div>
                <div><Label className="text-boco-navy font-semibold">Property Address</Label><Input className="mt-1.5 text-slate-900" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city" data-testid="pw-address" /></div>
                <div className="sm:col-span-2"><Label className="text-boco-navy font-semibold">Tell us about the job</Label><Textarea className="mt-1.5 text-slate-900" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Approx. size, surface type, how dirty, timing, etc." data-testid="pw-details" /></div>
                <button type="submit" disabled={submitting} className="sm:col-span-2 rounded-full bg-boco-green px-6 py-3.5 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors disabled:opacity-70 flex items-center justify-center gap-2" data-testid="pw-submit">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send My Request"}
                </button>
              </form>
              <p className="mt-3 text-center text-xs text-slate-400">Prefer to talk? Call or text {BRAND.phone}.</p>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
